/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../shared.enums';
import { islandHasCornerTrims } from './helpers';
import { Panels } from '../panels/panels.class';
import { PanelTrim } from './panel-trim.class';
import { PanelTrimType } from './enums';
import { Publish } from '../../decorators';
import { resetCfn } from './functions/reset.cfn';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { PanelTrimsDataIn, PanelTrimsDataOut } from './types';

export class PanelTrims extends KitchenDesignBase<PanelTrimsDataIn> {
	#children: Array<PanelTrim>;

	/**
	 * The constructor of the 'PanelTrims' class
	 * @param options provides context, also used to hydrate the instance
	 */
	constructor (options: KitchenDesignOptions<PanelTrimsDataIn> = {}) {
		super(BlueprintInstanceType.PanelTrims, options);

		const { dataIn: data = null } = options;

		this.#children = [];
		if (data && data.children && data.children.length > 0) {
			data.children.forEach((n) => this.#children.push(new PanelTrim({ dataIn: n, parent: this })));
		}

		this.setInitialized();
	}

	// MOKS islands have no separate trim hardware, so hide any stored records without deleting them (GitHub #467)
	get children (): Array<PanelTrim> { return islandHasCornerTrims(this.refMap.island) ? this.#children : [] }

	/**
	 * removes all trim and recreates trim for provided panels
	 * @param panels Panels (optional)
	 */
	@Publish('panel_trims', 'reset')
	public reset (panels: Panels = null): void { resetCfn(this as PanelTrims, panels); }

	/** Null whenever no trim joins the pair -- a MOKS island hides every record, so that is the norm, not an edge case. */
	public findByPanelIds (firstId: string, secondId: string = null): PanelTrim | null {
		const firstFilter = this.children.filter((pt) => pt.panels[0] === firstId || pt.panels[1] === firstId);
		if (firstFilter.length <= 1 || secondId === null) return (firstFilter[0] as PanelTrim) ?? null;
		return (firstFilter.find((pt) => pt.panels[0] === secondId || pt.panels[1] === secondId) as PanelTrim) ?? null;
	}

	/**
	 * Creates and adds a new PanelTrim
	 *
	 * @param panelTrimData serialized data to hydrate new instance with
	 * @returns Instance of PanelTrim
	 */
	public new (trimType: PanelTrimType, panels: [string, string] | [string]): PanelTrim {
		const panelTrim = new PanelTrim({
			dataIn: {
				trimType,
				panels
			},
			parent: this
		});
		this.#children.push(panelTrim);

		return panelTrim as PanelTrim;
	}

	/**
	 * removes id matches the provided id
	 * @param id id to be removed
	 */
	public delete (id: string): void {
		this.#children = this.#children.filter((pt) => {
			return pt.id !== id;
		});
	}

	/**
	 *
	 * @returns serialized data representing class state for persistence
	 */
	public serialize (): PanelTrimsDataOut {
		return {
			children: this.#children.map((panelTrim: PanelTrim) => panelTrim.serialize())
		};
	}
}
